/**
 * Fiche cuvée (/cave/cuvee/:id) — 3 colonnes : visuels, présentation + onglets
 * (Dégustation, Stock, Achats, Mouvements) et référence sélectionnée (prix, achat, options).
 * Le clic sur une case de la grille millésimes × formats met à jour le bloc de droite.
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer,
  AreaChart, Area, XAxis, ReferenceArea, ReferenceLine,
} from 'recharts';
import { MapPin, Download, X, ChevronRight, Archive, ArchiveRestore, Trash2, ShoppingBag, Plus, RefreshCw, Sparkles, Compass, PackagePlus, Mic, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { caveApi, vignoblesApi, metaApi, parametresApi, scannerApi, achatsApi } from '../../services/api';
import StockMovementPanel from '../cave/StockMovementPanel';
import AddWishModal from '../achats/AddWishModal';
import { usePublicMode } from '../../context/PublicModeContext';
import { useNavGuard } from '../../context/NavGuardContext';
import { useAuth } from '../../context/AuthContext';
import { LocationPicker, useLocationPicker } from '../../components/wine/LocationPicker';
import { formatEuro, formatDate, formatInt } from '../../utils/format';
import { Button, Spinner } from '../../components/ui/kit';
import { WineColorDot, Hearts, ColorKicker, SpecificityTags, MaskedValue } from '../../components/wine/wine';
import { InlineEditable, TagsEditor, CepagesEditor } from '../../components/wine/InlineEditable';
import { AppellationField, AppellationHint, ClassicCepagesButton } from '../../components/wine/AppellationField';
import { RegionField } from '../../components/wine/RegionField';
import { DomainField } from '../../components/wine/DomainField';
import { invalidateStock } from '../../services/refresh';
import { ImagePicker } from '../../components/wine/ImagePicker';
import { TASTING_AXES, WINE_TYPE_LABELS, BOTTLE_FORMAT_OPTIONS, TASTING_CONTEXTS } from '../../constants';
import { VoiceDictateButton } from '../../components/wine/VoiceDictateButton';
import WineMap from '../../components/map/WineMap';

/** Radar de dégustation (6 axes, 0-5) — rendu lecture seule. */
function RadarProfile({ radar }) {
  const data = TASTING_AXES.map((a) => ({ axis: a.label, value: radar?.[a.key] ?? 0 }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <RadarChart data={data} outerRadius="70%">
        <PolarGrid stroke="rgba(32,31,29,0.16)" />
        <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10, fill: '#5e5b5b' }} />
        <Radar dataKey="value" stroke="#b68235" fill="#b68235" fillOpacity={0.18} />
      </RadarChart>
    </ResponsiveContainer>
  );
}
// eslint-disable-next-line no-unused-vars
const _keepRadarProfile = RadarProfile;


/** Radar éditable à la souris : cliquer / glisser sur un axe règle sa valeur (0-5). Sans curseurs. */
export function RadarEditor({ radar: radarProp, resetKey, onSave, compact }) {
  const [radar, setRadar] = useState(radarProp || {});
  const svgRef = useRef(null);
  const dragging = useRef(false);
  useEffect(() => setRadar(radarProp || {}), [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const C = 100, R = 78;
  const angleOf = (i) => (-90 + i * (360 / TASTING_AXES.length)) * (Math.PI / 180);
  const point = (i, v) => [C + (v / 5) * R * Math.cos(angleOf(i)), C + (v / 5) * R * Math.sin(angleOf(i))];

  const apply = (clientX, clientY, commit) => {
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 200 - C;
    const y = ((clientY - rect.top) / rect.height) * 200 - C;
    const ang = (Math.atan2(y, x) * 180) / Math.PI;
    const step = 360 / TASTING_AXES.length;
    let i = Math.round((ang + 90) / step) % TASTING_AXES.length;
    if (i < 0) i += TASTING_AXES.length;
    const dist = Math.min(1, Math.hypot(x, y) / R);
    const next = { ...radar, [TASTING_AXES[i].key]: Math.max(0, Math.min(5, Math.round(dist * 5))) };
    setRadar(next);
    if (commit) onSave(next);
  };

  const values = TASTING_AXES.map((a, i) => point(i, radar?.[a.key] ?? 0));
  const polygon = values.map((p) => p.join(',')).join(' ');

  return (
    <div data-testid="radar-editor" className="group">
      <svg ref={svgRef} viewBox="0 0 200 200" className={`w-full ${compact ? 'max-w-[224px]' : 'max-w-[280px]'} mx-auto touch-none select-none cursor-crosshair`}
        onPointerDown={(e) => { dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId); apply(e.clientX, e.clientY, false); }}
        onPointerMove={(e) => { if (dragging.current) apply(e.clientX, e.clientY, false); }}
        onPointerUp={(e) => { dragging.current = false; apply(e.clientX, e.clientY, true); }}>
        <defs>
          <radialGradient id="radarGrad" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#d9a85a" stopOpacity={0.55} />
            <stop offset="100%" stopColor="#b68235" stopOpacity={0.12} />
          </radialGradient>
        </defs>
        {[1, 2, 3, 4, 5].map((ring) => (
          <polygon key={ring} points={TASTING_AXES.map((_, i) => point(i, ring).join(',')).join(' ')}
            fill="none" stroke="rgba(32,31,29,0.12)" />
        ))}
        {TASTING_AXES.map((a, i) => {
          const [ex, ey] = point(i, 5);
          const [lx, ly] = point(i, 6.1);
          return (
            <g key={a.key}>
              <line x1={C} y1={C} x2={ex} y2={ey} stroke="rgba(32,31,29,0.12)" />
              <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fontSize="8" fill="#5e5b5b">{a.label}</text>
            </g>
          );
        })}
        <polygon points={polygon} fill="url(#radarGrad)" stroke="#b68235" strokeWidth={1.6} strokeLinejoin="round" />
        {values.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r={4} fill="#b68235" stroke="#fff" strokeWidth={1.4} data-testid={`radar-point-${TASTING_AXES[i].key}`} />
        ))}
      </svg>
      <p className="text-[11px] text-n600 text-center mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Cliquez ou glissez sur un axe pour régler le profil.</p>
    </div>
  );
}

/** Éditeur de spécificités : chips retirables + menu d'ajout depuis la liste des Paramètres. */
function SpecificityEditor({ value, onSave }) {
  const [open, setOpen] = useState(false);
  const { data: available = [] } = useQuery({ queryKey: ['specificities'], queryFn: parametresApi.specificities });
  const labelOf = (k) => available.find((s) => s.key === k)?.label || k;
  const add = (key) => { if (!value.includes(key)) onSave([...value, key]); setOpen(false); };
  const remove = (key) => onSave(value.filter((k) => k !== key));
  return (
    <div className="flex flex-wrap items-center gap-1.5 relative" data-testid="specificity-editor">
      {value.map((k) => (
        <span key={k} className="vd-tag vd-tag-accent" data-testid={`spec-chip-${k}`}>
          {labelOf(k)}
          <button onClick={() => remove(k)} className="ml-1 text-accent-700 hover:text-red-700" data-testid={`spec-remove-${k}`}>×</button>
        </span>
      ))}
      <button onClick={() => setOpen((o) => !o)} className="vd-tag border-dashed hover:border-gold" data-testid="spec-add-btn">+ Ajouter</button>
      {open && (
        <div className="absolute top-7 left-0 z-20 bg-paper border border-hair rounded-md shadow-lg py-1 min-w-[180px] max-h-56 overflow-auto animate-fade-in" data-testid="spec-menu">
          {available.filter((s) => !value.includes(s.key)).map((s) => (
            <button key={s.key} onClick={() => add(s.key)} className="w-full text-left px-3 h-8 text-[13px] hover:bg-surface" data-testid={`spec-opt-${s.key}`}>{s.label}</button>
          ))}
          {available.filter((s) => !value.includes(s.key)).length === 0 && <p className="px-3 py-2 text-[12px] text-n500">Toutes ajoutées</p>}
        </div>
      )}
    </div>
  );
}

/** Éditeur de garde éditable à la souris (années relatives au millésime).
 *  Glisser le sommet règle l'apogée ; glisser les bords de la zone grise règle la fenêtre (from/to).
 *  `baseYear` : année de référence pour l'axe (millésime le plus ancien, sinon année courante). */
export function GardeEditor({ garde, resetKey, onSave, baseYear, compact }) {
  const def = { from: 2, to: 12, apogee: 7 };
  // Normalise la garde : garantit des bornes et surtout un `apogee` numérique
  // (certaines gardes IA/héritées ne portent que {from,to} → l'étiquette n'affichait que « ans »).
  const normGarde = (gg) => {
    if (!gg || !Number.isFinite(Number(gg.to))) return def;
    const from = Number.isFinite(Number(gg.from)) ? Number(gg.from) : def.from;
    const to = Number(gg.to);
    const apogee = Number.isFinite(Number(gg.apogee)) ? Number(gg.apogee) : Math.round((from + to) / 2);
    return { from, to, apogee };
  };
  const [g, setG] = useState(normGarde(garde));
  const svgRef = useRef(null);
  const drag = useRef(null); // 'from' | 'to' | 'apogee'
  useEffect(() => setG(normGarde(garde)), [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const MAXT = Math.max(16, (g.to || 12) + 4);
  const W = 280, H = compact ? 100 : 124, PAD = 12, BOTTOM = 18, TOP = 20;
  const xOf = (yr) => PAD + (yr / MAXT) * (W - 2 * PAD);
  const sigma = Math.max(1.1, ((g.to - g.from) || 8) / 3.2);
  const yOf = (yr) => (H - BOTTOM) - Math.exp(-((yr - g.apogee) ** 2) / (2 * sigma * sigma)) * (H - BOTTOM - TOP);
  const pts = [];
  for (let yr = 0; yr <= MAXT; yr += 0.5) pts.push(`${xOf(yr).toFixed(1)},${yOf(yr).toFixed(1)}`);
  const areaPath = `M${xOf(0).toFixed(1)},${H - BOTTOM} L` + pts.join(' L') + ` L${xOf(MAXT).toFixed(1)},${H - BOTTOM} Z`;
  const ticks = []; for (let yr = 0; yr <= MAXT; yr += 2) ticks.push(yr);
  const base = Number(baseYear) || new Date().getFullYear();

  const yearAt = (clientX) => {
    const rect = svgRef.current.getBoundingClientRect();
    const yr = ((clientX - rect.left) / rect.width * W - PAD) / (W - 2 * PAD) * MAXT;
    return Math.max(0, Math.min(MAXT, Math.round(yr)));
  };
  const pick = (yr) => {
    // Choisit la poignée la plus proche : bord gauche (from), bord droit (to) ou sommet (apogée).
    const d = { from: Math.abs(yr - g.from), to: Math.abs(yr - g.to), apogee: Math.abs(yr - g.apogee) };
    return Object.keys(d).reduce((a, b) => (d[b] < d[a] ? b : a));
  };
  const apply = (clientX, commit) => {
    const yr = yearAt(clientX);
    let next = { ...g };
    if (drag.current === 'from') next.from = Math.max(0, Math.min(yr, g.apogee - 1));
    else if (drag.current === 'to') next.to = Math.max(g.apogee + 1, Math.min(MAXT, yr));
    else next.apogee = Math.max(g.from + 1, Math.min(g.to - 1, yr));
    setG(next); if (commit) onSave(next);
  };

  return (
    <div data-testid="garde-editor" className="group">
      <p className="kicker mb-1">Garde · à boire entre {g.from} et {g.to} ans · apogée {g.apogee} ans</p>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full max-w-[380px] mx-auto block touch-none select-none cursor-crosshair"
        onPointerDown={(e) => { drag.current = pick(yearAt(e.clientX)); e.currentTarget.setPointerCapture(e.pointerId); apply(e.clientX, false); }}
        onPointerMove={(e) => { if (drag.current) apply(e.clientX, false); }}
        onPointerUp={(e) => { if (drag.current) { apply(e.clientX, true); drag.current = null; } }}>
        <defs>
          <linearGradient id="gardeGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#d9a85a" stopOpacity={0.5} /><stop offset="100%" stopColor="#b68235" stopOpacity={0.04} />
          </linearGradient>
        </defs>
        <rect x={xOf(g.from)} y={TOP - 6} width={Math.max(0, xOf(g.to) - xOf(g.from))} height={H - BOTTOM - TOP + 6} fill="rgba(182,130,53,0.10)" />
        <line x1={PAD} y1={H - BOTTOM} x2={W - PAD} y2={H - BOTTOM} stroke="rgba(32,31,29,0.16)" />
        {ticks.map((yr) => (
          <g key={yr}>
            <line x1={xOf(yr)} y1={H - BOTTOM} x2={xOf(yr)} y2={H - BOTTOM + 3} stroke="rgba(32,31,29,0.3)" />
            <text x={xOf(yr)} y={H - 4} textAnchor="middle" fontSize="9" fill="#5e5b5b">{base + yr}</text>
          </g>
        ))}
        <path d={areaPath} fill="url(#gardeGrad)" stroke="#b68235" strokeWidth={1.6} strokeLinejoin="round" />
        {/* poignées de fenêtre (bords de la zone grise) */}
        <circle cx={xOf(g.from)} cy={H - BOTTOM} r={4} fill="#fff" stroke="#b68235" strokeWidth={1.6} data-testid="garde-from" />
        <circle cx={xOf(g.to)} cy={H - BOTTOM} r={4} fill="#fff" stroke="#b68235" strokeWidth={1.6} data-testid="garde-to" />
        {/* apogée + label années (x bridé pour ne jamais être coupé par les bords du graphe) */}
        <line x1={xOf(g.apogee)} y1={yOf(g.apogee)} x2={xOf(g.apogee)} y2={H - BOTTOM} stroke="#7d5411" strokeDasharray="3 3" />
        <text x={Math.min(W - 20, Math.max(20, xOf(g.apogee)))} y={yOf(g.apogee) - 7} textAnchor="middle" fontSize="9" fontWeight="600" fill="#7d5411" data-testid="garde-apogee-label">{`${g.apogee} ans`}</text>
        <circle cx={xOf(g.apogee)} cy={yOf(g.apogee)} r={4.5} fill="#7d5411" stroke="#fff" strokeWidth={1.4} data-testid="garde-apogee" />
      </svg>
      <p className="text-[11px] text-n600 text-center mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Glissez le sommet (apogée) ou les bords de la zone grise (fenêtre de garde).</p>
    </div>
  );
}


/** Courbe de garde : fenêtre d'apogée en fond, repère « aujourd'hui ». */
function AgingCurve({ vintage }) {
  const now = new Date().getFullYear();
  const from = vintage.drink_from || (Number(vintage.year) + 2);
  const to = vintage.drink_before || (Number(vintage.year) + 12);
  const apogee = vintage.apogee || Math.round((from + to) / 2);
  const sigma = Math.max(1.5, (to - from) / 3);
  const data = [];
  for (let y = from; y <= to; y++) {
    data.push({ year: y, q: Math.exp(-((y - apogee) ** 2) / (2 * sigma * sigma)) });
  }
  return (
    <div>
      <p className="kicker mb-1">Garde · {vintage.year} · à boire avant {to}</p>
      <ResponsiveContainer width="100%" height={120}>
        <AreaChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 8 }}>
          <defs>
            <linearGradient id="ag" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#b68235" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#b68235" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#7d7979' }} interval="preserveStartEnd"
            ticks={[from, apogee, to]} axisLine={{ stroke: 'rgba(32,31,29,0.16)' }} tickLine={false} />
          <ReferenceArea x1={Math.max(from, apogee - 2)} x2={Math.min(to, apogee + 3)} fill="#f2e3c8" fillOpacity={0.6} />
          <ReferenceLine x={Math.min(Math.max(now, from), to)} stroke="#7d5411" strokeDasharray="3 3"
            label={{ value: `▲ ${now}`, position: 'top', fontSize: 10, fill: '#7d5411' }} />
          <Area dataKey="q" stroke="#b68235" strokeWidth={1.5} fill="url(#ag)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Normalise un millésime : 2 chiffres → 4 (00–30 ⇒ 20xx, 31–99 ⇒ 19xx). Lettres (XO…) conservées. */
function normYear(v) {
  const s = String(v || '').trim();
  if (/^\d{2}$/.test(s)) { const n = parseInt(s, 10); return String(n <= 30 ? 2000 + n : 1900 + n); }
  return s;
}

/** Grille millésimes (lignes) × formats (colonnes). Clic sur une case = sélection.
 *  Chaque case se coupe en deux au survol : « Ajuster » (quantité) · « Emplacement » (rangement). */
function StockGrid({ vintages, references, formats, formatLabels, selectedRefId, onSelect, cuveeId, onRefresh, picker, readOnly }) {
  const [edit, setEdit] = useState(null); // refId en cours d'édition (quantité)
  const [val, setVal] = useState('');
  const [newCell, setNewCell] = useState(null); // `${year}:${fmt}` case vide en saisie
  const [adding, setAdding] = useState(false);
  const [newV, setNewV] = useState({ year: '', format: '750', quantity: 6 });
  const [showArchived, setShowArchived] = useState(false);
  const cell = (year, fmt) => references.find((r) => r.year === year && r.format === fmt);
  const allFormats = formats.length ? formats : ['750'];
  const colTotals = allFormats.map((f) => references.filter((r) => r.format === f).reduce((s, r) => s + r.quantity, 0));
  const grand = references.reduce((s, r) => s + r.quantity, 0);
  const vintageOf = (ref) => vintages.find((v) => v.id === ref.vintage_id) || vintages.find((v) => v.year === ref.year);
  const stockOf = (v) => references.filter((r) => r.year === v.year).reduce((s, r) => s + r.quantity, 0);
  const archivedCount = vintages.filter((v) => v.archived).length;
  const visibleVintages = vintages.filter((v) => showArchived || !v.archived);

  const toggleArchive = async (v) => {
    await caveApi.updateVintage(v.id, { archived: !v.archived });
    onRefresh();
    toast.success(v.archived ? 'Millésime désarchivé' : 'Millésime archivé');
  };

  const commit = async (ref) => {
    setEdit(null);
    const q = parseInt(val, 10);
    if (!Number.isNaN(q) && q !== ref.quantity) { await caveApi.setRefQuantity(ref.id, q); onRefresh(); toast.success('Stock ajusté'); }
  };
  const createCell = async (year, fmt) => {
    const q = parseInt(val, 10);
    setNewCell(null);
    if (!Number.isNaN(q) && q > 0) {
      await scannerApi.entry({ cuvee_id: cuveeId, year: String(year), format: fmt, quantity: q });
      onRefresh(); toast.success('Format ajouté');
    }
  };
  const addVintage = async () => {
    const year = normYear(newV.year);
    if (!year) return;
    await scannerApi.entry({ cuvee_id: cuveeId, year: String(year), format: newV.format, quantity: Number(newV.quantity) });
    setAdding(false); setNewV({ year: '', format: '750', quantity: 6 }); onRefresh(); toast.success('Millésime ajouté');
  };

  const locBadge = (ref) => {
    const locs = ref.locations || [];
    if (!locs.length) return '—';
    const extra = locs.length > 1 ? ` +${locs.length - 1}` : '';
    return `${locs[0].label || locs[0].code}${extra}`;
  };

  return (
    <div>
      <table className="w-full text-[13px] nums" data-testid="stock-grid">
        <thead>
          <tr className="kicker">
            <th className="text-left py-2"></th>
            {allFormats.map((f) => <th key={f} className="py-2 px-1 text-center font-medium">{formatLabels[f] || f}</th>)}
            <th className="py-2 px-1 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {visibleVintages.map((v) => {
            const rowTotal = references.filter((r) => r.year === v.year).reduce((s, r) => s + r.quantity, 0);
            return (
              <tr key={v.id} className={`hair-t ${v.archived ? 'opacity-60' : ''}`}>
                <td className="py-2 pr-2 font-title text-lg">
                  <span className="inline-flex items-center gap-1.5">
                    {v.year}
                    {!readOnly && rowTotal === 0 && (
                      <button onClick={() => toggleArchive(v)} title={v.archived ? 'Désarchiver ce millésime' : 'Archiver ce millésime (sans stock)'}
                        data-testid={`vintage-archive-${v.id}`} className="text-n500 hover:text-gold700">
                        {v.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                      </button>
                    )}
                  </span>
                </td>
                {allFormats.map((f) => {
                  const ref = cell(v.year, f);
                  const ck = `${v.year}:${f}`;
                  if (!ref) {
                    return (
                      <td key={f} className="py-2 px-1">
                        {newCell === ck ? (
                          <input autoFocus type="number" min="0" value={val} data-testid={`grid-new-${ck}`}
                            onChange={(e) => setVal(e.target.value)} onBlur={() => createCell(v.year, f)}
                            onKeyDown={(e) => { if (e.key === 'Enter') createCell(v.year, f); if (e.key === 'Escape') setNewCell(null); }}
                            className="w-full h-12 rounded border border-gold text-center text-[15px] nums" />
                        ) : (
                          <button data-testid={`grid-empty-${ck}`} disabled={readOnly}
                            onClick={() => { if (readOnly) return; setNewCell(ck); setVal(''); }}
                            className="w-full h-12 rounded border border-dashed border-hair flex items-center justify-center text-n500 hover:bg-surface">—</button>
                        )}
                      </td>
                    );
                  }
                  const sel = ref.id === selectedRefId;
                  const editing = edit === ref.id;
                  return (
                    <td key={f} className="py-2 px-1">
                      {editing ? (
                        <input autoFocus type="number" min="0" value={val} data-testid={`grid-edit-${ref.id}`}
                          onChange={(e) => setVal(e.target.value)} onBlur={() => commit(ref)}
                          onKeyDown={(e) => { if (e.key === 'Enter') commit(ref); if (e.key === 'Escape') setEdit(null); }}
                          className="w-full h-12 rounded border border-gold text-center text-[15px] nums" />
                      ) : (
                        <div className={`group relative w-full h-12 rounded border flex overflow-hidden transition-colors ${sel ? 'vd-selected border-gold' : 'border-hair'}`} data-testid={`grid-cell-${ref.id}`}>
                          {/* Moitié gauche — quantité + Ajuster */}
                          <button data-testid={`grid-adjust-${ref.id}`}
                            onClick={() => { onSelect(ref.id); if (!readOnly) { setEdit(ref.id); setVal(String(ref.quantity)); } }}
                            className="flex-1 flex flex-col items-center justify-center hover:bg-surface leading-none">
                            <span className="text-[15px]">{ref.quantity}</span>
                            <span className="text-[9px] text-gold700 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">Ajuster</span>
                          </button>
                          <span className="w-px bg-hair group-hover:bg-gold/50 self-stretch my-1.5" />
                          {/* Moitié droite — emplacement */}
                          <button data-testid={`grid-location-${ref.id}`}
                            onClick={(e) => { onSelect(ref.id); if (!readOnly) picker.openAt(e.currentTarget, ref, vintageOf(ref)); }}
                            aria-expanded={picker.isOpen && picker.state?.ref?.id === ref.id}
                            className="flex-1 flex flex-col items-center justify-center hover:bg-surface leading-tight px-0.5 min-w-0">
                            <span className="text-[10px] text-n700 break-words text-center w-full" title={locBadge(ref)}>{locBadge(ref)}</span>
                            <span className="text-[9px] text-gold700 opacity-0 group-hover:opacity-100 transition-opacity">Emplacement</span>
                          </button>
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="py-2 px-1 text-right text-n700">{rowTotal}</td>
              </tr>
            );
          })}
          <tr className="hair-t kicker">
            <td className="py-2">Total</td>
            {colTotals.map((t, i) => <td key={i} className="py-2 px-1 text-center">{t}</td>)}
            <td className="py-2 px-1 text-right">{grand}</td>
          </tr>
        </tbody>
      </table>
      {adding ? (
        <div className="flex flex-wrap items-end gap-2 mt-3 vd-card p-2.5" data-testid="add-vintage-form">
          <label className="block"><span className="text-[11px] text-n500">Millésime</span>
            <input className="vd-input h-8 w-24 text-[13px]" value={newV.year} onChange={(e) => setNewV({ ...newV, year: e.target.value })} onBlur={() => setNewV((p) => ({ ...p, year: normYear(p.year) }))} placeholder="2022" data-testid="add-vintage-year" /></label>
          <label className="block"><span className="text-[11px] text-n500">Format</span>
            <select className="vd-input h-8 text-[13px]" value={newV.format} onChange={(e) => setNewV({ ...newV, format: e.target.value })}>
              {BOTTLE_FORMAT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select></label>
          <label className="block"><span className="text-[11px] text-n500">Quantité</span>
            <input type="number" min="1" className="vd-input h-8 w-20 text-[13px] nums" value={newV.quantity} onChange={(e) => setNewV({ ...newV, quantity: e.target.value })} data-testid="add-vintage-qty" /></label>
          <Button variant="primary" className="h-8" onClick={addVintage} data-testid="add-vintage-save">Ajouter</Button>
          <Button className="h-8" onClick={() => setAdding(false)}>Annuler</Button>
        </div>
      ) : !readOnly && (
        <div className="mt-3 flex items-center gap-4">
          <button onClick={() => setAdding(true)} className="text-[13px] text-gold700 hover:underline flex items-center gap-1" data-testid="add-vintage-btn">
            <Plus className="w-4 h-4" /> Ajouter un millésime
          </button>
          {archivedCount > 0 && (
            <button onClick={() => setShowArchived((s) => !s)} data-testid="toggle-archived-vintages"
              title={`${showArchived ? 'Masquer' : 'Afficher'} les millésimes archivés (${archivedCount})`}
              aria-label={`${showArchived ? 'Masquer' : 'Afficher'} les millésimes archivés (${archivedCount})`}
              className={`text-[13px] flex items-center ${showArchived ? 'text-gold700' : 'text-n500 hover:text-n700'}`}>
              {showArchived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Historique des achats d'une référence (entrées de stock + corrections). */
function PurchaseHistory({ refId }) {
  const { data, isLoading } = useQuery({ queryKey: ['ref-entries', refId], queryFn: () => caveApi.refEntries(refId), enabled: !!refId });
  if (!refId) return <p className="text-n500 italic text-[13px]">Sélectionnez une référence pour voir ses achats.</p>;
  if (isLoading) return <Spinner />;
  const entries = data?.entries || [];
  return (
    <div className="text-[13.5px]" data-testid="purchase-history">
      <div className="flex items-center justify-between mb-2">
        <p className="kicker">Historique des achats</p>
        {!data?.financials_hidden && (
          <p className="text-[12px] text-n600">PMP {formatEuro(data?.pmp)} · dernier {formatEuro(data?.last_purchase)}</p>
        )}
      </div>
      {!entries.length ? <p className="text-n500 italic">Aucun mouvement d'entrée pour cette référence.</p> : (
        <div className="space-y-1.5">
          {entries.map((e) => (
            <div key={e.id} className="flex justify-between gap-2 hair-b py-1.5" data-testid={`purchase-${e.id}`}>
              <span className="min-w-0">
                <span className={e.type === 'correction' ? 'text-amber-700' : ''}>{e.type === 'correction' ? 'Ajustement' : 'Entrée'} · {e.quantity > 0 ? `+${e.quantity}` : e.quantity}</span>
                <span className="text-n500"> · {formatDate(e.date)}{e.user_name ? ` · ${e.user_name}` : ''}</span>
                {e.document && (
                  <span className="text-gold700"> · {e.document.type} {e.document.supplier_name || ''} {e.document.number ? `N°${e.document.number}` : ''}</span>
                )}
              </span>
              {e.unit_price != null && <span className="nums text-n600 shrink-0">{formatEuro(e.unit_price)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Ligne du bloc « Prix de vente » — éditable (centimes acceptés) au clic. */
function PriceRow({ label, price, info, testId, onSave, editable }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const start = () => { if (editable && onSave) { setVal(price ?? ''); setEditing(true); } };
  const commit = async () => {
    setEditing(false);
    const num = val === '' ? null : Number(String(val).replace(',', '.'));
    if (num !== (price ?? null) && !(num === null && price == null)) await onSave(num);
  };
  return (
    <div className="flex items-baseline justify-between py-1.5" data-testid={testId}>
      <span className="text-[14px] text-n700">{label}</span>
      <span className="flex items-baseline gap-3">
        {editing ? (
          <input autoFocus type="text" inputMode="decimal" value={val}
            onChange={(e) => setVal(e.target.value.replace(/[^0-9.,]/g, ''))} onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
            className="vd-input h-7 w-24 text-right nums text-base" data-testid={`${testId}-input`} placeholder="0,00" />
        ) : (
          <span role={editable ? 'button' : undefined} onClick={start} data-testid={`${testId}-value`}
            className={`text-xl nums ${editable && onSave ? 'cursor-text rounded px-0.5 hover:outline hover:outline-1 hover:outline-dashed hover:outline-gold' : ''}`}>
            {formatEuro(price)}
          </span>
        )}
        {info && <span className="text-[12px] text-n500 nums w-16 text-right">{info}</span>}
      </span>
    </div>
  );
}

export default function FicheCuvee() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isPublic } = usePublicMode();
  const { user } = useAuth();
  const readOnlyLoc = user?.role === 'serveur';
  const [selectedRefId, setSelectedRefId] = useState(null);
  const [tab, setTab] = useState('degustation');
  const [mapOpen, setMapOpen] = useState(false);
  const [movePanel, setMovePanel] = useState({ open: false, mode: 'sortie' });
  const [wishOpen, setWishOpen] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [toCaveOpen, setToCaveOpen] = useState(false);
  const { setGuard } = useNavGuard();

  const { data, isLoading } = useQuery({ queryKey: ['cuvee', id], queryFn: () => caveApi.cuvee(id) });
  const { data: tagSug = [] } = useQuery({ queryKey: ['tags'], queryFn: caveApi.tags });
  const { data: cepageSug = [] } = useQuery({ queryKey: ['cepages'], queryFn: caveApi.cepages });
  const { data: suppliers = [] } = useQuery({ queryKey: ['achats-suppliers'], queryFn: achatsApi.suppliers });
  const { data: locData } = useQuery({ queryKey: ['location-spaces'], queryFn: caveApi.locationsData });
  const spaces = locData?.spaces || [];
  const picker = useLocationPicker({ qc, cuveeId: id, spaces });

  // Mode brouillon : garde de navigation « Abandonner ? » + suppression du brouillon si abandon.
  const isDraft = !!data?.cuvee?.draft;
  useEffect(() => {
    if (!isDraft) { setGuard(null); return undefined; }
    setGuard({
      message: 'Abandonner la création de cette fiche bouteille ? La fiche non créée sera supprimée.',
      onConfirm: () => { caveApi.deleteCuvee(id).catch(() => {}); },
    });
    const bu = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', bu);
    return () => { setGuard(null); window.removeEventListener('beforeunload', bu); };
  }, [isDraft, id, setGuard]);

  const selectedRef = useMemo(() => {
    if (!data) return null;
    if (selectedRefId) return data.references.find((r) => r.id === selectedRefId);
    return data.references.find((r) => r.format === '750' && r.quantity > 0) || data.references[0] || null;
  }, [data, selectedRefId]);

  if (isLoading || !data) return <Spinner />;

  const { cuvee, domain, vintages, references, formats_present, total_stock, financials_hidden } = data;
  const masked = isPublic || financials_hidden;
  const isDeguste = cuvee.catalog_status === 'degustee_seulement';
  const formatLabels = Object.fromEntries(references.map((r) => [r.format, r.format_label]));
  const selectedVintage = vintages.find((v) => v.id === selectedRef?.vintage_id) || vintages.find((v) => v.year === selectedRef?.year) || vintages[0];

  const setHearts = async (n) => { await caveApi.setHearts(cuvee.id, n); qc.invalidateQueries({ queryKey: ['cuvee', id] }); invalidateStock(qc); };
  const patchCuvee = async (patch) => { await caveApi.updateCuvee(cuvee.id, patch); qc.invalidateQueries({ queryKey: ['cuvee', id] }); invalidateStock(qc); };
  const archiveCuvee = async () => { await caveApi.archiveCuvee(cuvee.id, !cuvee.archived); qc.invalidateQueries({ queryKey: ['cuvee', id] }); invalidateStock(qc); toast.success(cuvee.archived ? 'Cuvée désarchivée' : 'Cuvée archivée'); };
  const deleteCuvee = async () => {
    if (!window.confirm(`Supprimer définitivement « ${cuvee.name} » et son stock ? Cette action est irréversible.`)) return;
    await caveApi.deleteCuvee(cuvee.id); invalidateStock(qc); toast.success('Cuvée supprimée'); navigate('/cave/stock');
  };
  const patchVintage = async (patch) => { if (selectedVintage) { await caveApi.updateVintage(selectedVintage.id, patch); qc.invalidateQueries({ queryKey: ['cuvee', id] }); invalidateStock(qc); } };

  // Vin dégusté : actions de la colonne de droite.
  const decouvrirDomaine = async () => { await caveApi.degusteDecouvrir(cuvee.id); toast.success('Domaine ajouté « à découvrir »'); };
  const deleteDeguste = async () => {
    if (!window.confirm(`Supprimer « ${cuvee.name} » des vins dégustés ?`)) return;
    await caveApi.deleteDeguste(cuvee.id); invalidateStock(qc); qc.invalidateQueries({ queryKey: ['degustes'] });
    toast.success('Vin dégusté supprimé'); navigate('/cave/degustes');
  };
  const appendToField = (field, current) => (text) => patchCuvee({ [field]: current ? `${current}\n${text}` : text });
  const patchRef = async (patch) => { if (selectedRef) { await caveApi.updateReference(selectedRef.id, patch); qc.invalidateQueries({ queryKey: ['cuvee', id] }); invalidateStock(qc); } };
  const recomputePrices = async () => {
    if (!selectedRef) return;
    try { await caveApi.recomputeRefPrices(selectedRef.id); qc.invalidateQueries({ queryKey: ['cuvee', id] }); invalidateStock(qc); toast.success('Prix de vente recalculés'); }
    catch (e) { toast.error(e.response?.data?.detail || e.message || 'Recalcul impossible'); }
  };
  const saveTags = async (tags) => { await caveApi.setTags(cuvee.id, tags); qc.invalidateQueries({ queryKey: ['cuvee', id] }); qc.invalidateQueries({ queryKey: ['tags'] }); };
  const COLOR_OPTS = ['rouge', 'blanc', 'rose', 'orange', 'gris', 'vert'].map((c) => ({ value: c, label: c[0].toUpperCase() + c.slice(1) }));
  const TYPE_OPTS = Object.entries(WINE_TYPE_LABELS).map(([value, label]) => ({ value, label }));
  const toggleFlag = async (key) => {
    if (!selectedRef) return;
    await caveApi.setFlags(cuvee.id, { reference_id: selectedRef.id, [key]: !selectedRef[key] });
    qc.invalidateQueries({ queryKey: ['cuvee', id] }); invalidateStock(qc);
  };

  const emporterDiff = selectedRef?.price_table && selectedRef?.price_emporter ? selectedRef.price_table - selectedRef.price_emporter : null;

  const completeWithAI = async () => {
    setEnriching(true);
    try {
      const r = await caveApi.completeCuveeAI(cuvee.id);
      qc.invalidateQueries({ queryKey: ['cuvee', id] }); invalidateStock(qc);
      toast.success(r.updated?.length ? `Champs complétés : ${r.updated.length}` : 'Rien à compléter — tout est renseigné');
    } catch (e) { toast.error(e.response?.data?.detail || 'Complétion IA impossible'); }
    finally { setEnriching(false); }
  };
  const createDraft = async () => {
    if (!cuvee.name?.trim()) { toast.error('Saisissez au moins le nom de la cuvée'); return; }
    await caveApi.updateCuvee(cuvee.id, { draft: false });
    setGuard(null);
    qc.invalidateQueries({ queryKey: ['cuvee', id] }); invalidateStock(qc);
    toast.success('Fiche bouteille créée');
  };
  const cancelDraft = async () => {
    setGuard(null);
    await caveApi.deleteCuvee(cuvee.id).catch(() => {});
    invalidateStock(qc);
    navigate('/cave/stock');
  };

  const aiButton = (
    <Button onClick={completeWithAI} disabled={enriching} data-testid="enrich-cuvee">
      <Sparkles className="w-4 h-4" strokeWidth={1.6} /> {enriching ? '…' : "Compléter avec l'IA"}
    </Button>
  );

  return (
    <div className="p-4 md:p-6 pb-[150px] md:pb-[150px] max-w-full overflow-x-hidden">
      {/* Fil d'Ariane + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="text-[13px]">
          <Link to="/cave/stock" onClick={(e) => { if (isDraft) { e.preventDefault(); cancelDraft(); } }} className="vd-link">La cave</Link>
          <span className="text-n500"> / {cuvee.name || (isDraft ? 'Nouvelle bouteille' : '')}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {isDraft ? (
            <>
              {aiButton}
              <Button onClick={cancelDraft} data-testid="draft-cancel">Annuler</Button>
              <Button variant="primary" onClick={createDraft} disabled={!cuvee.name?.trim()} data-testid="draft-create"><Plus className="w-4 h-4" strokeWidth={1.6} /> Créer</Button>
            </>
          ) : (
            <>
              {aiButton}
              <Button onClick={() => setMapOpen(true)} data-testid="voir-sur-carte"><MapPin className="w-4 h-4" strokeWidth={1.6} /> Voir sur la carte</Button>
              <Button onClick={() => setWishOpen(true)} data-testid="add-wish-btn"><ShoppingBag className="w-4 h-4" strokeWidth={1.6} /> Ajouter dans à commander</Button>
              {!isDeguste && <Button onClick={() => selectedRef && setMovePanel({ open: true, mode: 'sortie' })} data-testid="sortie">Sortie</Button>}
              {!isDeguste && <Button variant="primary" onClick={() => selectedRef && setMovePanel({ open: true, mode: 'entree' })} data-testid="entree"><Download className="w-4 h-4" strokeWidth={1.6} /> Entrée</Button>}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[280px_1fr_320px] lg:gap-6">
        {/* Colonne 1 (desktop) — contents sur mobile pour réordonner photos / cépages */}
        <div className="contents lg:block lg:min-w-0">
          {/* Visuels — sur mobile : une seule ligne (vignettes ≤ 200px) ; sur desktop : empilés */}
          <div className="order-2 lg:order-none grid grid-cols-3 gap-3 max-w-[480px] lg:max-w-none lg:grid-cols-2">
            <div className="lg:col-span-2">
              <ImagePicker label="Photo étiquette" kind="label" cuveeId={cuvee.id} fileId={cuvee.label_photo}
                onUploaded={() => qc.invalidateQueries({ queryKey: ['cuvee', id] })} aspect="aspect-square lg:aspect-[3/4]" />
            </div>
            <ImagePicker label="Contre-étiq." kind="back_label" cuveeId={cuvee.id} fileId={cuvee.back_label_photo}
              onUploaded={() => qc.invalidateQueries({ queryKey: ['cuvee', id] })} aspect="aspect-square" />
            <ImagePicker label="Logo" kind="logo" cuveeId={cuvee.id} fileId={domain?.logo}
              onUploaded={() => qc.invalidateQueries({ queryKey: ['cuvee', id] })} aspect="aspect-square" />
          </div>
          <div className={`order-4 lg:order-none lg:block ${tab === 'infos' ? '' : 'hidden'}`}>
            <dl className="mt-4 text-[13px] space-y-2">
              <div><dt className="text-n500 mb-1">Cépages</dt>
                <dd><CepagesEditor cepages={cuvee.cepages || []} suggestions={cepageSug}
                  onChange={(cepages) => patchCuvee({ cepages })} />
                  {(cuvee.cepages || []).length === 0 && (
                    <ClassicCepagesButton appellation={cuvee.appellation} color={cuvee.color}
                      onApply={(arr) => patchCuvee({ cepages: arr })} />
                  )}
                </dd></div>
              <div className="flex justify-between items-center"><dt className="text-n500">Degré</dt>
                <dd><InlineEditable type="number" value={cuvee.degre} onSave={(v) => patchCuvee({ degre: v })} placeholder="—" inputClassName="h-7 w-20 text-[13px]" testId="degre" /></dd></div>
              <div className="flex justify-between"><dt className="text-n500">Ajoutée le</dt><dd className="nums">{formatDate(cuvee.added_at)}</dd></div>
            </dl>
            <div className="mt-3">
              <TagsEditor tags={cuvee.tags || []} suggestions={tagSug} onChange={saveTags} />
            </div>
          </div>
        </div>

        {/* Colonne 2 (desktop) — contents sur mobile pour réordonner header / onglets / cépages */}
        <div className="contents lg:block lg:min-w-0">
          {/* Présentation (titre, domaine, appellation…) */}
          <div className="order-1 lg:order-none">
          <ColorKicker color={cuvee.color} type={cuvee.type} appellation={cuvee.appellation} region={cuvee.region} />
          <h1 className="font-title text-5xl leading-none mt-2 flex items-center gap-3 flex-wrap">
            <InlineEditable value={cuvee.name} onSave={(v) => patchCuvee({ name: v })} testId="name" inputClassName="text-3xl" />
            {isDeguste && <span className="vd-tag vd-tag-fill font-sans text-[12px] self-center" data-testid="deguste-tag">Vin dégusté</span>}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            <DomainField cuveeId={cuvee.id} domainName={domain?.name} linkTo={domain?.id ? `/vignobles/domaine/${domain.id}` : undefined}
              linkState={{ fromCuvee: { id: cuvee.id, name: cuvee.name } }}
              onLinked={() => { qc.invalidateQueries({ queryKey: ['cuvee', id] }); invalidateStock(qc); }} testId="cuvee-domain" />
            <Hearts count={cuvee.hearts} onChange={setHearts} />
            <span className="ml-auto flex items-center gap-1">
              <button onClick={archiveCuvee} className="vd-btn vd-btn-ghost h-8 text-[12px]" data-testid="cuvee-archive" title={cuvee.archived ? 'Désarchiver' : 'Archiver'}>
                <Archive className="w-4 h-4" /> {cuvee.archived ? 'Désarchiver' : 'Archiver'}
              </button>
              <button onClick={deleteCuvee} className="vd-btn vd-btn-ghost h-8 text-[12px] text-red-700" data-testid="cuvee-delete" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[13px]">
            <span className="flex items-center gap-1"><span className="text-n500">Couleur</span><InlineEditable type="select" value={cuvee.color} options={COLOR_OPTS} onSave={(v) => patchCuvee({ color: v })} testId="color" /></span>
            <span className="flex items-center gap-1"><span className="text-n500">Type</span><InlineEditable type="select" value={cuvee.type} options={TYPE_OPTS} onSave={(v) => patchCuvee({ type: v })} testId="type" /></span>
            <span className="flex items-center gap-1"><span className="text-n500">Appellation</span><AppellationField value={cuvee.appellation} onSave={(v) => patchCuvee({ appellation: v })} onPick={({ vignoble }) => { if (vignoble && !cuvee.region) patchCuvee({ region: vignoble }); }} testId="appellation" inputClassName="h-7 text-[13px]" /></span>
            <span className="flex items-center gap-1"><span className="text-n500">Région</span><RegionField value={cuvee.region} onSave={(v) => patchCuvee({ region: v })} testId="region" inputClassName="h-7 text-[13px]" /></span>
          </div>
          <AppellationHint value={cuvee.appellation} />
          {(cuvee.appellation || '').trim().toLowerCase() === 'vin de france' && (
            <div className="mt-1.5 flex items-center gap-1 text-[13px]" data-testid="appellation-assimilee-row">
              <span className="text-n500">Assimilé à</span>
              <AppellationField value={cuvee.appellation_assimilee} onSave={(v) => patchCuvee({ appellation_assimilee: v })}
                onPick={({ vignoble }) => { if (vignoble && !cuvee.region) patchCuvee({ region: vignoble }); }}
                testId="appellation-assimilee" inputClassName="h-7 text-[13px]" />
            </div>
          )}

          <div className="mt-3 flex items-start gap-2">
            <span className="kicker pt-1.5">Spécificités</span>
            <SpecificityEditor value={cuvee.specificities || []} onSave={(arr) => patchCuvee({ specificities: arr })} />
          </div>

          <div className="mt-4 py-3 hair-t hair-b">
            <p className="kicker mb-1 flex items-center gap-2 not-italic">
              Présentation
              <VoiceDictateButton onTranscribe={appendToField('presentation', cuvee.presentation)} testId="presentation-mic" />
            </p>
            <div className="italic text-[15px] leading-relaxed text-justify text-n700 font-title">
              <InlineEditable type="textarea" value={cuvee.presentation} onSave={(v) => patchCuvee({ presentation: v })}
                placeholder="Présentation œnologique…" testId="presentation" displayClassName="block w-full" inputClassName="w-full not-italic" />
            </div>
          </div>

          </div>

          {/* Onglets */}
          <div className="order-3 lg:order-none flex gap-5 mt-4 hair-b">
            {[['degustation', 'Dégustation'], ['infos', 'Infos'], ['stock', `Stock et emplacements · ${total_stock}`], ['achats', 'Achats'], ['mouvements', 'Mouvements']].map(([k, l]) => (
              <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                className={`pb-2 -mb-px text-[13.5px] border-b-2 transition-colors ${k === 'infos' ? 'lg:hidden ' : ''}${tab === k ? 'border-gold text-ink' : 'border-transparent text-n600 hover:text-ink'}`}>
                {l}
              </button>
            ))}
          </div>

          <div className="order-5 lg:order-none mt-4">
            {tab === 'degustation' && (
              <div className="deg-charts-wrap">
              <div className="deg-charts">
                <div>
                  {selectedVintage
                    ? <RadarEditor radar={selectedVintage.radar} resetKey={selectedVintage.id} onSave={(radar) => patchVintage({ radar })} />
                    : <RadarEditor radar={cuvee.radar} resetKey={cuvee.id} onSave={(radar) => patchCuvee({ radar })} />}
                </div>
                <div>
                  {selectedVintage
                    ? <GardeEditor resetKey={selectedVintage.id} baseYear={Number(selectedVintage.year)}
                        garde={{
                          from: (selectedVintage.drink_from || Number(selectedVintage.year) + 2) - Number(selectedVintage.year),
                          to: (selectedVintage.drink_before || Number(selectedVintage.year) + 12) - Number(selectedVintage.year),
                          apogee: (selectedVintage.apogee || Math.round(((selectedVintage.drink_from || Number(selectedVintage.year) + 2) + (selectedVintage.drink_before || Number(selectedVintage.year) + 12)) / 2)) - Number(selectedVintage.year),
                        }}
                        onSave={(gg) => patchVintage({ drink_from: Number(selectedVintage.year) + gg.from, drink_before: Number(selectedVintage.year) + gg.to, apogee: Number(selectedVintage.year) + gg.apogee })} />
                    : <GardeEditor garde={cuvee.garde} resetKey={cuvee.id}
                        baseYear={vintages.length ? Math.min(...vintages.map((v) => Number(v.year)).filter(Boolean)) : undefined}
                        onSave={(garde) => patchCuvee({ garde })} />}
                  {vintages.some((v) => v.notes?.length) && (
                    <>
                      <p className="kicker mt-4 mb-1">Millésimes dégustés</p>
                      <div className="space-y-2">
                        {vintages.filter((v) => v.notes?.length).map((v) => v.notes.map((n, i) => (
                          <div key={v.id + i} className="flex gap-3 text-[13px]">
                            <span className="font-title text-lg w-10">{v.year}</span>
                            <span className="text-n700">{n.text} <span className="text-n500">— {n.date}</span></span>
                          </div>
                        )))}
                      </div>
                    </>
                  )}
                  <p className="kicker mt-4 mb-1 flex items-center gap-2">
                    Accords suggérés
                    <VoiceDictateButton onTranscribe={(t) => patchCuvee({ accords: [...(cuvee.accords || []), ...t.split(',').map((s) => s.trim()).filter(Boolean)] })} testId="accords-mic" />
                  </p>
                  <div className="text-[13px] text-n700">
                    <InlineEditable value={(cuvee.accords || []).join(', ')} testId="accords" inputClassName="w-full h-8 text-[13px]"
                      placeholder="Ajouter des accords (séparés par des virgules)…"
                      onSave={(v) => patchCuvee({ accords: v.split(',').map((s) => s.trim()).filter(Boolean) })} />
                  </div>
                </div>
                <div className="deg-charts-full">
                  <p className="kicker mb-1 flex items-center gap-2">
                    Note
                    <VoiceDictateButton onTranscribe={appendToField('deg_note', cuvee.deg_note)} testId="deg-note-mic" />
                  </p>
                  <InlineEditable type="textarea" value={cuvee.deg_note} testId="deg-note"
                    placeholder="Note de dégustation libre…" displayClassName="block w-full min-h-[40px] text-[13px] text-n700"
                    inputClassName="w-full text-[13px]" onSave={(v) => patchCuvee({ deg_note: v })} />
                </div>
              </div>
              </div>
            )}
            {tab === 'stock' && (
              <div>
                <StockGrid vintages={vintages} references={references} formats={formats_present}
                  formatLabels={formatLabels} selectedRefId={selectedRef?.id} onSelect={setSelectedRefId}
                  cuveeId={cuvee.id} picker={picker} readOnly={readOnlyLoc}
                  onRefresh={() => { qc.invalidateQueries({ queryKey: ['cuvee', id] }); qc.invalidateQueries({ queryKey: ['stock'] }); qc.invalidateQueries({ queryKey: ['location-spaces'] }); }} />
                <p className="text-[12.5px] text-n500 mt-3">Cliquez une case pour ses prix · « ajuster » pour corriger le stock · <span className="vd-link">Voir sur le plan de cave</span></p>
              </div>
            )}
            {tab === 'achats' && (
              <div className="text-[13.5px]">
                {masked ? <p className="text-n500 italic">Achats masqués (écran public)</p> : <PurchaseHistory refId={selectedRef?.id} />}
              </div>
            )}
            {tab === 'mouvements' && <RefMovements refId={selectedRef?.id} />}
          </div>
        </div>

        {/* Colonne 3 — Référence sélectionnée */}
        <div className="order-6 lg:order-none lg:border-l lg:border-hair lg:pl-5 lg:min-w-0">
          {isDeguste ? (
            <div data-testid="deguste-column">
              <div className="mt-1 space-y-2">
                <Button variant="primary" className="w-full justify-center" onClick={() => setToCaveOpen(true)} data-testid="deguste-to-cave"><PackagePlus className="w-4 h-4" /> Ajouter à la cave</Button>
                <Button className="w-full justify-center" onClick={decouvrirDomaine} data-testid="deguste-decouvrir"><Compass className="w-4 h-4" /> Domaine à découvrir</Button>
                <Button className="w-full justify-center text-red-700" onClick={deleteDeguste} data-testid="deguste-delete"><Trash2 className="w-4 h-4" /> Supprimer</Button>
              </div>
              <div className="mt-5 hair-t pt-4 space-y-3 text-[14px]">
                <p className="kicker">Dégustation</p>
                <div className="flex items-center justify-between gap-2"><span className="text-n500">Contexte</span>
                  <InlineEditable type="select" value={cuvee.tasting_context} options={TASTING_CONTEXTS} onSave={(v) => patchCuvee({ tasting_context: v })} testId="deg-context" /></div>
                <div className="flex items-center justify-between gap-2"><span className="text-n500">Lieu</span>
                  <InlineEditable value={cuvee.place} onSave={(v) => patchCuvee({ place: v })} testId="deg-place" placeholder="—" inputClassName="h-7 text-[13px]" /></div>
                <div className="flex items-center justify-between gap-2"><span className="text-n500">Date</span>
                  <InlineEditable value={cuvee.tasting_date} onSave={(v) => patchCuvee({ tasting_date: v })} testId="deg-date" placeholder="—" inputClassName="h-7 text-[13px]" /></div>
              </div>
              {!masked && (
                <div className="hair-t mt-3">
                  <PriceRow testId="deg-purchase-ht" label="Prix d'achat HT" price={cuvee.purchase_price_ht} editable onSave={(v) => patchCuvee({ purchase_price_ht: v })} />
                  <PriceRow testId="deg-price-public" label="Prix TTC public constaté" price={cuvee.price_seen} editable onSave={(v) => patchCuvee({ price_seen: v })} />
                </div>
              )}
            </div>
          ) : (
          <>
          <div className="flex items-baseline justify-between">
            <span className="text-[13px] text-n500">Stock total</span>
            <span className="font-title text-5xl leading-none">{total_stock}</span>
          </div>
          {selectedRef && (
            <>
              <div className="mt-4 hair-t pt-3">
                {references.length > 1 && <p className="kicker mb-1">Millésime — prix édités ci-dessous</p>}
                <div className="flex items-baseline justify-between gap-2">
                  {references.length > 1 ? (
                    <select className="vd-input h-9 text-[15px] font-title flex-1 max-w-[220px] border-gold/60 bg-gold100/40 cursor-pointer" value={selectedRef.id}
                      onChange={(e) => setSelectedRefId(e.target.value)} data-testid="ref-select">
                      {references.map((r) => {
                        const label = `${r.year} · ${r.format_label}${r.quantity ? '' : ' (rupture)'}`;
                        return <option key={r.id} value={r.id}>{label}</option>;
                      })}
                    </select>
                  ) : (
                    <span className="font-title text-2xl">{selectedRef.year} · {selectedRef.format_label}</span>
                  )}
                  <span className="text-[13px] text-n600 nums whitespace-nowrap">
                    {selectedRef.quantity} btl ·{' '}
                    {readOnlyLoc ? (
                      <span>{selectedRef.location_label || 'non rangé'}</span>
                    ) : (
                      <button data-testid="ref-location-link" aria-expanded={picker.isOpen && picker.state?.ref?.id === selectedRef.id}
                        onClick={(e) => picker.openAt(e.currentTarget, selectedRef, selectedVintage, 'right')}
                        className="underline decoration-dotted underline-offset-2 hover:text-gold700 hover:decoration-solid transition-colors">
                        {selectedRef.location_label || 'non rangé'}{(selectedRef.locations?.length || 0) > 1 ? ` +${selectedRef.locations.length - 1}` : ''}
                      </button>
                    )}
                  </span>
                </div>
              </div>

              <p className="kicker mt-4 mb-1">Prix de vente</p>
              <div className="hair-t">
                <PriceRow testId="price-table" label="Service à table" price={selectedRef.price_table} editable={!masked} onSave={(v) => patchRef({ price_table: v })} info={selectedRef.coefficient_table ? `× ${selectedRef.coefficient_table.toLocaleString('fr-FR')}` : null} />
                <PriceRow testId="price-emporter" label="À emporter" price={selectedRef.price_emporter} editable={!masked} onSave={(v) => patchRef({ price_emporter: v })} info={emporterDiff ? `−${emporterDiff} €` : null} />
                <PriceRow testId="price-caviste" label="Caviste" price={selectedRef.price_caviste} editable={!masked} onSave={(v) => patchRef({ price_caviste: v })} info={selectedRef.coefficient_caviste ? `× ${selectedRef.coefficient_caviste.toLocaleString('fr-FR')}` : null} />
                <PriceRow testId="price-verre" label="Au verre (12 cl)" price={selectedRef.price_verre} editable={!masked} onSave={(v) => patchRef({ price_verre: v })} info={selectedRef.verres ? `${selectedRef.verres} verres` : null} />
              </div>

              <p className="kicker mt-4 mb-1">Achat</p>
              <div className="hair-t text-[13.5px]" data-testid="achat-block">
                {masked ? (
                  <p className="text-n500 italic py-2">Achat et valeur masqués (écran public)</p>
                ) : (
                  <>
                    <PriceRow testId="purchase-ht" label="Prix d'achat HT" price={selectedRef.purchase_price_ht} editable onSave={(v) => patchRef({ purchase_price_ht: v })} />
                    {selectedRef.purchase_price_ht != null && (
                      <div className="flex items-center justify-between py-1">
                        <button onClick={recomputePrices} data-testid="recompute-prices-btn"
                          className="text-[13px] text-gold700 hover:underline flex items-center gap-1">
                          <RefreshCw className="w-3.5 h-3.5" /> Recalculer les prix de vente
                        </button>
                        {selectedRef.price_forced && (
                          <span className="text-[11px] text-gold800 bg-gold100 rounded-full px-2 py-0.5" data-testid="price-forced-badge" title="Prix saisis manuellement — non recalculés lors d'un recalcul global">
                            Prix forcé
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex justify-between py-1.5 items-center">
                      <span className="text-n700">Fournisseur</span>
                      <select className="vd-input h-7 text-[13px] max-w-[170px]" value={selectedRef.supplier_name || 'Direct domaine'}
                        onChange={(e) => patchRef({ supplier_name: e.target.value })} data-testid="supplier-select">
                        <option value="Direct domaine">Direct domaine</option>
                        {suppliers.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                      </select>
                    </div>
                    <PriceRow testId="estimated-value" label="Valeur estimée" price={selectedRef.estimated_value} editable onSave={(v) => patchRef({ estimated_value: v })} />
                    <PriceRow testId="price-public" label="Prix public" price={selectedRef.price_public} editable onSave={(v) => patchRef({ price_public: v })} />
                    {selectedRef.purchase_price_ht > 0 && selectedRef.price_table > 0 && (
                      <div className="flex justify-between py-1.5 items-baseline text-[13px] bg-surface/50 -mx-1 px-1 rounded" data-testid="marge-coef">
                        <span className="text-n600">Marge & coefficient</span>
                        <span className="nums text-n800">
                          × {(selectedRef.price_table / selectedRef.purchase_price_ht).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
                          {' · '}marge {formatEuro(selectedRef.price_table - selectedRef.purchase_price_ht)}
                          {' ('}{Math.round((1 - selectedRef.purchase_price_ht / selectedRef.price_table) * 100)} %{')'}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mt-4 space-y-2">
                <label className="flex items-center gap-2 text-[14px] cursor-pointer" data-testid="flag-wine-list">
                  <input type="checkbox" checked={!!selectedRef.on_wine_list} onChange={() => toggleFlag('on_wine_list')} className="accent-[#b68235] w-4 h-4" />
                  Intégrer à la carte des vins
                </label>
                <label className="flex items-center gap-2 text-[14px] cursor-pointer" data-testid="flag-by-glass">
                  <input type="checkbox" checked={!!selectedRef.by_glass} onChange={() => toggleFlag('by_glass')} className="accent-[#b68235] w-4 h-4" />
                  Proposer au verre
                </label>
              </div>

              <div className="mt-4 vd-card p-3 text-[12.5px]">
                <p className="text-n500 mb-1">Sur la carte, cette ligne s'affichera :</p>
                <p className="flex items-center gap-1">
                  <span className="font-title text-base">{cuvee.name}, {selectedRef.year} – {domain?.name}</span>
                  {cuvee.hearts > 0 && <Hearts count={cuvee.hearts} size={11} />}
                </p>
                <div className="flex gap-1 mt-1">
                  {cuvee.specificities?.slice(0, 2).map((s) => <span key={s} className="vd-tag text-[10px] py-0">{s}</span>)}
                </div>
              </div>
            </>
          )}
          </>
          )}
        </div>
      </div>

      {mapOpen && <MapPanel domain={domain} onClose={() => setMapOpen(false)} navigate={navigate} />}
      {wishOpen && (
        <AddWishModal preset={{ cuvee_id: cuvee.id, cuvee_name: cuvee.name, domain_id: domain?.id, domain_name: domain?.name, color: cuvee.color, year: selectedRef?.year, format: selectedRef?.format }} onClose={() => setWishOpen(false)} />
      )}
      {movePanel.open && (
        <StockMovementPanel cuveeId={cuvee.id} initialRefId={selectedRef?.id} mode={movePanel.mode}
          asDrawer onClose={() => setMovePanel({ open: false, mode: movePanel.mode })} />
      )}
      {toCaveOpen && (
        <DegusteToCaveModal cuvee={cuvee} onClose={() => setToCaveOpen(false)} onDone={() => {
          setToCaveOpen(false);
          qc.invalidateQueries({ queryKey: ['cuvee', id] }); qc.invalidateQueries({ queryKey: ['degustes'] }); invalidateStock(qc);
          toast.success('Bouteille ajoutée à la cave');
        }} />
      )}
      <LocationPicker picker={picker} references={references} readOnly={readOnlyLoc} />
    </div>
  );
}

/** Journal des mouvements d'une référence (onglet Mouvements). */
function RefMovements({ refId }) {
  const { data = [] } = useQuery({ queryKey: ['ref-mvt', refId], queryFn: () => caveApi.refMovements(refId), enabled: !!refId });
  if (!refId) return <p className="text-n500 text-[13px]">Sélectionnez une référence.</p>;
  if (data.length === 0) return <p className="text-n500 text-[13px]">Aucun mouvement.</p>;
  return (
    <div className="text-[13px]">
      {data.map((m) => (
        <div key={m.id} className="flex justify-between hair-b py-1.5">
          <span>{formatDate(m.date)} · {m.type}{m.motif ? ` · ${m.motif}` : ''}</span>
          <span className={`nums ${m.quantity > 0 ? 'text-gold700' : ''}`}>{m.quantity > 0 ? '+' : ''}{m.quantity}</span>
        </div>
      ))}
    </div>
  );
}

/** Volet latéral « Où est le domaine » (carte + chiffres). */
function MapPanel({ domain, onClose, navigate }) {
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: metaApi.settings });
  const { data: detail } = useQuery({ queryKey: ['domain', domain?.id], queryFn: () => vignoblesApi.domain(domain.id), enabled: !!domain?.id });
  const home = settings?.gps || [44.324, 3.596];
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="map-panel">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="relative w-full max-w-[400px] bg-paper hair-l border-l border-hair h-full shadow-lg animate-fade-in flex flex-col">
        <div className="flex items-center justify-between px-4 h-12 hair-b">
          <span className="kicker">Où est le domaine</span>
          <button onClick={onClose} data-testid="close-map-panel"><X className="w-4 h-4" strokeWidth={1.6} /></button>
        </div>
        <div className="h-56 shrink-0"><WineMap domains={domain?.gps ? [{ ...domain, status: 'cave' }] : []} center={domain?.gps} zoom={8} home={home} selectedId={domain?.id} /></div>
        <div className="p-4 overflow-auto">
          <h3 className="font-title text-3xl">{domain?.name}</h3>
          <p className="text-[13px] text-n600 mt-1">{domain?.appellations?.join(' · ')} · {domain?.region}</p>
          <p className="text-[13px] text-n500">{domain?.dept} · environ {domain?.distance_km} km de la Maison</p>
          <dl className="mt-4 text-[14px] space-y-2 hair-t pt-3">
            <div className="flex justify-between"><dt className="text-n600">Cuvées en cave</dt><dd className="nums">{detail?.stats?.cuvees_in_cave ?? '—'}</dd></div>
            <div className="flex justify-between"><dt className="text-n600">Bouteilles</dt><dd className="nums">{detail?.stats?.bottles ?? '—'}</dd></div>
            <div className="flex justify-between"><dt className="text-n600">Sur la carte des vins</dt><dd className="nums">{detail?.stats?.on_wine_list ?? '—'}</dd></div>
          </dl>
        </div>
        <div className="mt-auto p-4 flex gap-2 hair-t">
          <Button className="flex-1" onClick={() => navigate('/vignobles')}>Carte complète</Button>
          <Button variant="primary" className="flex-1" onClick={() => navigate(`/vignobles/domaine/${domain?.id}`)}>Fiche domaine <ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
}


/** Mini-modale : convertit un vin dégusté en bouteille en cave (stock possible = 0). */
function DegusteToCaveModal({ cuvee, onClose, onDone }) {
  const [year, setYear] = useState((cuvee.tasting_date || '').slice(0, 4) || '');
  const [format, setFormat] = useState('750');
  const [quantity, setQuantity] = useState(0);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await caveApi.degusteToCave(cuvee.id, { year, format, quantity }); onDone(); }
    catch (e) { toast.error(e.message || 'Échec'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 animate-fade-in" onClick={onClose}>
      <div className="bg-paper border border-hair rounded-lg shadow-xl p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="to-cave-modal">
        <h3 className="font-title text-2xl mb-1 flex items-center gap-2"><PackagePlus className="w-5 h-5 text-gold700" /> Ajouter à la cave</h3>
        <p className="text-[13px] text-n600 mb-4">{cuvee.name}{cuvee.domain_name ? ` · ${cuvee.domain_name}` : ''}</p>
        <div className="grid grid-cols-3 gap-2">
          <label className="block"><span className="text-[12px] text-n600">Millésime</span>
            <input className="vd-input h-9" value={year} onChange={(e) => setYear(e.target.value)} placeholder="ex. 2021" data-testid="to-cave-year" /></label>
          <label className="block"><span className="text-[12px] text-n600">Format</span>
            <select className="vd-input h-9" value={format} onChange={(e) => setFormat(e.target.value)} data-testid="to-cave-format">
              {BOTTLE_FORMAT_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select></label>
          <label className="block"><span className="text-[12px] text-n600">Quantité</span>
            <input type="number" min="0" className="vd-input h-9 nums" value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} data-testid="to-cave-quantity" /></label>
        </div>
        <p className="text-[12px] text-n500 mt-2">La quantité peut rester à 0 : la bouteille rejoint la cave sans stock.</p>
        <div className="flex justify-end gap-2 mt-5">
          <Button onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy} data-testid="to-cave-save">Ajouter</Button>
        </div>
      </div>
    </div>
  );
}
